/**
 * shell/SettingsPanel.tsx —— **设置面板本体**（U-S1 形态重做：两列＋页头＋滚动面＋账号分节）。
 *
 * 真源（102，逐字）：
 *  · `components/settings/SettingsPanel.vue:418-452` —— 面板 `width:min(800px,100vw−48px)`／
 *    `height:min(800px,100vh−48px)`／`radius:32`／elevation；内部**左分区导航 188px**（行 40／
 *    r12／14px）＋**内容列**（页头 54／滚动面 padding 0 24 24）；层容器 fixed inset 0＋面板
 *    `align-self:center`＋`translateX((sidebar_w−rightbar_w)/2)`＝按主区居中（壳件承担，见 `SettingsDialog.tsx`）。
 *  · **U-S1 订正（20261004 大王令 §5「去 dsh 学」）**：~~底部常驻动作区（`pin:'footer'`，旧树
 *    `SettingsPanel.vue:48-51`）~~ ⇒ **账号＝设置内 section**（DSH `dsh-client-ui-settings-account`
 *    README.zh 逐字：账号页面显示登录状态、头像 **32px** 正圆；**退出登录＝普通动作按钮、非常驻**）；
 *    **侧栏底部账号入口**（头像 24／折叠 36×36／未登录＝「更多」三点行＋账号菜单含退出登录）见 `shell/Sidebar.tsx`。
 *    总口径＝「常驻只承载已结算记录，不承载待决动作」（判据 `ui_resident_is_position_only`）。
 *  · `ConversationRoot.vue:116-117`（0919 令逐字）——「用户信息 → 设置面板「关于」组显示行；
 *    登出 → 同组动作行；**常驻界面不留身份位**」。
 *  · `AppShellHost.vue:1528` —— 面板本体落 `shell.overlay`（§3.0 铁律 3）。
 *
 * 纪律（承任务书硬口径）：
 *  · 组与枚举**全部来自 `shell.decl.yaml`**（`settings.nav` 分节表＋`settings.accounts`；禁在本件写死清单）；
 *  · 写入只经 `PreferencesStore`（带 revision 围栏；无第二写路径）；登出走 `transport/session.ts::logout()`；
 *  · **零取数**（模型清单缺席 ⇒ 缺席呈现，不补造；actor 缺失 ⇒ 显式留痕「未提供」，禁填假值——
 *    旧树 `groups.js::ABOUT_FIELDS` 纪律）；
 *  · 分节切换＝**显示态**（S6 归内存，不持久、不入偏好；旧树 `activeGroup`/`v-show` 逐字——
 *    全部分节常留 DOM，隐藏≠卸载，控件草稿值不丢）。
 */
import React, { useEffect, useRef, useState } from "react";
import ModelCatalogSection, {
  type ModelCatalogHandle, type ModelFormValues, type ModelGroup,
} from "./ModelCatalogSection";
import { aboutLines } from "../theme/buildInfo";
import { deriveCatalog, type RawModel } from "../theme/modelCatalog";
import { connTextOf } from "./connText";
import { usePreferences } from "../theme/PreferencesProvider";
import { currentActor, logout } from "../transport/session";
import type { SettingGroupDecl, SettingValueDecl } from "./shellDecl";
import { assertNoRetiredSettings, SHELL_DECL, settingGroup } from "./shellDecl";
import { E_CODES } from "../transport/errorText";

/** 账号头像（DSH「以正圆图片显示账号头像，直径分别 24px/32px」；新树头像来源＝品牌件
 *  `assets/sononux-avatar.svg`，逐字承 U-L1 登录页同一件——零新资产） */
const AVATAR_URL = new URL("../assets/sononux-avatar.svg", import.meta.url).href;

/** 作用面标注（§7·S3） */
export const SCOPE_LABELS: Record<SettingGroupDecl["scope"], string> = {
  global: "全局即时",
  future_sessions: "影响未来会话",
  current_session: "仅当前会话",
  read_only: "只读",
};

export interface ModelProviderGroup {
  provider: string;
  models: Array<{ id: string; label: string }>;
}

/** `settings.nav` ＋ `settings.accounts` 的本地扩展型（声明单源＝`shell.decl.yaml`；
 *  `shellDecl.ts` 在本批工作面外 ⇒ 只读断言，不改其闭集校验）。 */
interface SettingsNavDecl { id: string; label: string }
interface SettingsAccountsDecl {
  /** 账号面所属分节（＝`nav[].id`；订正 §3.1「账号＝设置内 section」） */
  section: string;
  display: { id: string; label: string; source: string; in_group: string };
  /** 登出动作（**非 pin footer**：普通动作按钮，落账号分节内——订正 §3.3） */
  action: { id: string; label: string; data_item_key: string; pin?: string };   // ★20261005 pin:'footer'＝落左导航底部常驻动作区（102 groups.js 逐字）
}
const SETTINGS_EXTRA = SHELL_DECL.settings as typeof SHELL_DECL.settings & {
  nav?: SettingsNavDecl[];
  accounts?: SettingsAccountsDecl;
};
/** 分节表（顺序＝声明序＝渲染序；缺失 ⇒ 空导航＝声明期问题，由新用例判红兜住） */
export const SETTINGS_NAV: SettingsNavDecl[] = SETTINGS_EXTRA.nav ?? [];
/** 账号面声明（显示行落「关于」节；账号节＝`section` 指向的分节，登出＝节内普通动作）。
 *  装载期守卫：缺失 ⇒ 载入即抛（声明面是契约——不静默降级，同 `shellDecl` 纪律）。 */
const ACCOUNTS: SettingsAccountsDecl = (() => {
  const declared = SETTINGS_EXTRA.accounts;
  if (!declared) throw new Error(E_CODES.SHELL_DECL + "：settings.accounts 缺失（U-S1 订正 §3）");
  return declared;
})();

export interface SettingsPanelProps {
  /** 打开时**定位到哪一节**（＝`nav[].id`；★20261011：输入框模型位点开 ⇒ 直达「模型」节；
      缺省 ⇒ 首节。本 prop 只在**挂载时**生效——设置面板按需挂载，故每次打开都随调用方意图）。 */
  initialSection?: string;
  /** 默认模型的**供应商分组**（来自投影；缺席 ⇒ 缺席呈现，**零取数、不补造**） */
  modelGroups?: ModelProviderGroup[];
  /** 连接状态（与壳 header 同源：S1） */
  status: string;
  onReconnect?: () => void;
  onExportDiagnostics?: () => void;
  /** 会话级视图偏好可选面（＝视图族；由壳注入，本件不造清单） */
  viewOptions?: Array<{ name: string; title: string }>;
  /** 当前会话（缺省 ⇒ 会话级偏好呈**无会话**禁用态） */
  sessionId?: string;
  /** 页头 ✕（关闭＝宿主职责；旧树 `emit('close')` 的 React 等价） */
  onClose?: () => void;
  /** 用户信息显示行的注入面（宿主有事实则注入；缺省 ⇒ 本件经 `transport/session.ts::currentActor()`
   *  **服务端回执**懒读一次——硬口径5「actor，来自服务端回执」；失败 ⇒ 「未提供」留痕，禁假值） */
  actor?: string;
  /** 登出执行注入面（缺省 ⇒ 走 `transport/session.ts::logout()`；成功回执后整页回登录门） */
  onLogout?: () => void | Promise<void>;
  /** 出网注入（测试可换；只透传给 `currentActor`/`logout` 缺省路径） */
  fetchFn?: typeof fetch;
  /** 模型录入三动作注入面（返回 Promise ⇒ 以落定判成败：成功 ⇒ 经 ref `closeForm()` 命令式回程收表单；
   *  同步抛／Promise 拒 ⇒ **保留已填内容**）。onDeleteModel 直通（删除无表单回程）。
   *  🔴 未接线：端点待 A1-B —— 模型增/改/删后端端点尚未提供 ⇒ 生产链路缺省即「未接线」：
   *  零请求、**禁造本地假实现**（不建内存清单、不假造行）；端点就绪后由宿主注入实装。 */
  onAddModel?: (values: ModelFormValues) => void | Promise<void>;
  onUpdateModel?: (values: ModelFormValues & { id: string }) => void | Promise<void>;
  onDeleteModel?: (id: string) => void | Promise<void>;
  /** 提交中（宿主置位；表单禁用防重复提交） */
  busy?: boolean;
  /** 模型面错误（宿主注入；**可见提示**——禁静默失败） */
  modelError?: string;
}

/** 录入保存回程（③ 命令式）：动作成功 ⇒ 经 `ref` 调 `closeForm()` 收起录入表单；
 *  失败（同步抛／Promise 拒）⇒ **保留已填内容**（不收表单、不吞为成功）。 */
function saveThenClose(
  catalogRef: React.MutableRefObject<ModelCatalogHandle | null>,
  action: () => void | Promise<void>,
): void {
  let done: void | Promise<void>;
  try {
    done = action();
  } catch {
    return;                       // 失败：保留已填内容
  }
  void Promise.resolve(done).then(
    () => { catalogRef.current?.closeForm(); },
    () => { /* 失败：保留已填内容（不收表单） */ },
  );
}

/** 单组控件渲染（控件种类＝声明；越集不成组 ⇒ 声明期已拒；行为面承 U-S1 前实装，零改动） */
function GroupBody(props: {
  group: SettingGroupDecl;
  panel: SettingsPanelProps;
}): React.ReactElement {
  const { group, panel } = props;
  const { store, snapshot, update, setSessionView } = usePreferences();
  const values = snapshot.values;
  /** 录入表单宿主句柄（③ 保存成功 ⇒ 经 ref 命令式 `closeForm()`；无条件建＝hooks 纪律） */
  const catalogRef = useRef<ModelCatalogHandle | null>(null);

  if (group.control === "radio") {
    const current = (values as unknown as Record<string, string>)[group.id];
    return (
      <div className="xg-settings-row" role="radiogroup" aria-label={group.title}>
        {(group.values ?? []).map((value: SettingValueDecl) => (
          <button
            key={value.id}
            type="button"
            className="xg-choice"
            role="radio"
            aria-checked={current === value.id}
            aria-pressed={current === value.id}
            data-testid={"setting-" + group.id + "-" + value.id}
            onClick={() => update({ [group.id]: value.id } as never)}
          >{value.label}</button>
        ))}
      </div>
    );
  }

  if (group.control === "range") {
    return (
      <div className="xg-settings-row">
        <input
          type="range"
          aria-label={group.title}
          data-testid="setting-font_size-range"
          min={group.min}
          max={group.max}
          step={1}
          value={values.font_size}
          onChange={(event) => update({ font_size: Number(event.target.value) })}
        />
        <output data-testid="setting-font_size-value">{values.font_size}</output>
        <span className="xg-settings-scope">{group.min}–{group.max}（默认 {group.default}）</span>
      </div>
    );
  }

  if (group.control === "grouped_select") {
    // 分组数据＝**既有声明/偏好读取面**（投影 `modelGroups` ＋ 偏好 `default_model`；零新数据源）；
    // 派生走 `theme/modelCatalog.deriveCatalog`（唯一派生点：模型位与设置面板喂同一返回值）。
    const models: RawModel[] | null = panel.modelGroups
      ? panel.modelGroups.flatMap((g) => g.models.map((m) => ({ id: m.id, name: m.label, provider: g.provider })))
      : null;
    const catalog = deriveCatalog({ models, current: values.default_model });
    const groups: ModelGroup[] = catalog.groups.map((g) => ({
      key: g.key,
      label: g.label,
      items: g.items.map((m) => ({ id: m.id, name: m.name, effort: m.effort })),
    }));
    const add = panel.onAddModel;
    const put = panel.onUpdateModel;
    return (
      <React.Fragment>
        <ModelCatalogSection
          ref={catalogRef}
          groups={groups}
          current={values.default_model}
          onSetDefault={(id) => {
            // 唯一写路径＝`PreferencesStore.update`（S2：**带 revision 围栏**，落后即拒；无第二写路径）
            store.update({ default_model: id }, snapshot.revision);
          }}
          onAddModel={add ? (v) => { saveThenClose(catalogRef, () => add(v)); } : undefined}
          onUpdateModel={put ? (v) => { saveThenClose(catalogRef, () => put(v)); } : undefined}
          onDeleteModel={panel.onDeleteModel}
          busy={panel.busy}
          modelError={panel.modelError}
        />
        {/* 缺席呈现：模型清单缺席 ⇒ 空列表＋如实提示句（零取数、不补造：不造数据、不假造行） */}
        {groups.length === 0 ? (
          <p className="xg-settings-scope" data-testid="setting-default_model-absent">
            暂无模型数据（数据缺席），请稍后重试
          </p>
        ) : null}
      </React.Fragment>
    );
  }

  if (group.control === "fixed") {
    return (
      <div className="xg-settings-row">
        <span data-testid="setting-busy_delivery-fixed">插话（steer）</span>
        <span className="xg-settings-scope">{group.note}</span>
      </div>
    );
  }

  if (group.control === "status_action") {
    return (
      <div className="xg-settings-row">
        {/* ★20261011（大王令「对齐旧树」）：原渲染**裸 token**（`open`）＝污染 ⇒ 走 `connText.ts` 人面文案（单一来源） */}
        <span data-testid="setting-connection-status" data-status={panel.status} title={panel.status}>{connTextOf(panel.status)}</span>
        <button
          type="button"
          className="xg-btn"
          data-testid="setting-connection-reconnect"
          onClick={panel.onReconnect}
        >立即重连</button>
      </div>
    );
  }

  if (group.control === "readonly") {
    return (
      <dl className="xg-settings-row" data-testid="setting-about">
        {aboutLines().map((line) => (
          <React.Fragment key={line.key}>
            <dt className="xg-settings-scope">{line.label}</dt>
            <dd data-testid={"setting-about-" + line.key}>{line.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }

  if (group.control === "action") {
    return (
      <div className="xg-settings-row">
        <button
          type="button"
          className="xg-btn"
          data-testid="setting-diagnostics-export"
          onClick={panel.onExportDiagnostics}
        >导出诊断</button>
      </div>
    );
  }

  // view_choice（§7 会话级视图偏好；持久化）
  const options = panel.viewOptions ?? [];
  const disabled = !panel.sessionId || options.length === 0;
  return (
    <div className="xg-settings-row" role="radiogroup" aria-label={group.title}>
      {options.map((option) => (
        <button
          key={option.name}
          type="button"
          className="xg-choice"
          role="radio"
          disabled={disabled}
          aria-checked={values.session_view[panel.sessionId ?? ""] === option.name}
          aria-pressed={values.session_view[panel.sessionId ?? ""] === option.name}
          data-testid={"setting-session_view-" + option.name}
          onClick={() => { if (panel.sessionId) setSessionView(panel.sessionId, option.name); }}
        >{option.title}</button>
      ))}
      {disabled ? <span className="xg-settings-scope">无会话：先新建或选择一个会话后再选</span> : null}
    </div>
  );
}

/** 单组行卡（标题＋作用面徽标＋控件——S3 标注与 `settings-group-<id>`/`settings-scope-<id>` 判据锚保留） */
function GroupRow(props: { group: SettingGroupDecl; panel: SettingsPanelProps }): React.ReactElement {
  const { group, panel } = props;
  return (
    <section
      className="xg-settings-group"
      data-testid={"settings-group-" + group.id}
      data-scope={group.scope}
      data-control={group.control}
    >
      <span className="xg-settings-title">{group.title}</span>
      {/* S3：作用面显式标注 */}
      <span className="xg-settings-scope" data-testid={"settings-scope-" + group.id}>{SCOPE_LABELS[group.scope]}</span>
      {group.note && group.control !== "fixed" ? <span className="xg-settings-scope">{group.note}</span> : null}
      <GroupBody group={group} panel={panel} />
    </section>
  );
}

/** 面板本体（旧树两列形态；壳层/居中/遮罩归 `SettingsDialog`） */
export function SettingsPanel(props: SettingsPanelProps): React.ReactElement {
  const groups = SHELL_DECL.settings.groups;
  // 渲染面**运行时自证**：任何组标题/枚举标签落入退役词表 ⇒ 抛（禁复列）
  assertNoRetiredSettings(groups.flatMap((g) => [g.title, ...(g.values ?? []).map((v) => v.label)]));

  // 当前分节＝**纯显示态**（S6：不持久、不入偏好、不发请求；旧树 `activeGroup` 逐字——默认首节）
  const [active, setActive] = useState<string>(props.initialSection ?? SETTINGS_NAV[0]?.id ?? "");
  const activeLabel = SETTINGS_NAV.find((section) => section.id === active)?.label ?? "";

  // 账号显示行：宿主注入优先；缺省 ⇒ 服务端回执**懒读**（面板挂载即打开时才发；失败＝留痕不假值）
  const [actor, setActor] = useState<string>(props.actor ?? "");
  useEffect(() => {
    if (props.actor !== undefined || actor !== "") return undefined;
    let alive = true;
    currentActor(props.fetchFn)
      .then((res) => { if (alive) setActor(res.actor); })
      .catch(() => { /* 缺失 ⇒ 「未提供」留痕（ABOUT_FIELDS 纪律），不弹错不假值 */ });
    return () => { alive = false; };
  }, [props.actor, props.fetchFn, actor]);

  // 账号节登出动作（`data-item-key=logout`）：busy 态＝行内可见（旧树 texts {idle:'退出登录', busy:'退出中'}；禁 confirm()）
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  async function onAccountAction(): Promise<void> {
    if (logoutBusy || !ACCOUNTS) return;
    setLogoutBusy(true);
    setLogoutError("");
    try {
      if (props.onLogout) await props.onLogout();
      else {
        await logout(props.fetchFn);
        // 会话 cookie 已清 ⇒ 回登录门（宿主 `App.tsx` 不在本批工作面 ⇒ 整页重载是唯一可达的诚实回程）
        if (typeof window !== "undefined") window.location.reload();
      }
    } catch {
      setLogoutError("退出登录失败，请重试");
    } finally {
      setLogoutBusy(false);
    }
  }

  return (
    <div className="xg-settings-panel" data-testid="settings-panel">
      {/* 左分区导航（188px；行 40／r12／14px＝硬口径3）
          ——旧树 `SettingsPanel.vue:33-46` 逐字：`nav`＋真按钮＋`aria-current`（非 tablist 语义）；
          U-S1 订正 §3.3：**撤底部常驻动作区**（登出移入「账号」分节，非常驻）。 */}
      <nav className="xg-settings-panel__nav" aria-label="设置分节">
        <div className="xg-settings-panel__nav-title">{SHELL_DECL.settings.title}</div>
        <div className="xg-settings-panel__nav-list">
          {SETTINGS_NAV.map((section) => (
            <button
              key={section.id}
              type="button"
              className="xg-settings-panel__nav-cell"
              data-nav={section.id}
              data-testid={"settings-nav-" + section.id}
              aria-current={section.id === active ? "true" : undefined}
              onClick={() => setActive(section.id)}
            >{section.label}</button>
          ))}
        </div>
        {/* 左导航**底部常驻动作区**（102 `SettingsPanel.vue:48-63` 逐结构：`pin:'footer'` 声明驱动；
            102 CSS `.settings-panel__footer`：flex column／gap 4／flex-shrink 0／margin-top 8／padding-top 8／
            border-top 0.5px／底走 surface-overlay ⇒ 内容列滚动时本区始终可见）。本轮由「账号」节回归此处。 */}
        {ACCOUNTS.action.pin === "footer" ? (
          <div className="xg-settings-panel__footer" data-testid="settings-footer">
            <button
              type="button"
              className="xg-btn xg-settings-panel__footer-action xg-touch-44"
              data-item-key={ACCOUNTS.action.data_item_key}
              data-testid="settings-logout"
              disabled={logoutBusy}
              onClick={() => { void onAccountAction(); }}
            >{logoutBusy ? "退出中" : ACCOUNTS.action.label}</button>
            {logoutError ? (
              /* 错误行 testid 取 `settings-account-error`（**不复用**登出钮的 testid 前缀）——
                 大王令自验口径：产物内登出钮 testid 命中数＝**1**（全 UI 唯一登出）；
                 错误行若复用同前缀，命中数变 2 ⇒ 自验假红。 */
              <p className="xg-settings-scope" data-testid="settings-account-error" role="alert">{logoutError}</p>
            ) : null}
          </div>
        ) : null}
      </nav>

      {/* 右内容列：页头 54（当前分节名＋✕）＋滚动面（padding 0 24 24） */}
      <div className="xg-settings-panel__content">
        <div className="xg-settings-panel__head">
          <span className="xg-settings-panel__section-title" data-testid="settings-section-title">{activeLabel}</span>
          <button
            type="button"
            className="xg-settings-panel__close"
            data-testid="settings-close"
            aria-label="关闭设置"
            onClick={props.onClose}
          >✕</button>
        </div>
        <div className="xg-settings-panel__body">
          {/* 分节页＝v-show 语义（全部常留 DOM，隐藏≠卸载——旧树 :31「切回分节不重建控件，草稿值不丢」） */}
          {SETTINGS_NAV.map((section) => (
            <div
              key={section.id}
              className="xg-settings-panel__page"
              data-page={section.id}
              data-testid={"settings-page-" + section.id}
              style={section.id === active ? undefined : { display: "none" }}
            >
              {groups.filter((group) => group.area === section.id).map((group) => (
                <GroupRow key={group.id} group={group} panel={props} />
              ))}
              {/* 硬口径5：用户信息＝「关于」组内**显示行**（testid 避 `settings-group-` 前缀＝11 组计数判据不受扰） */}
              {section.id === ACCOUNTS.display.in_group ? (
                <div className="xg-settings-group" data-testid="settings-accounts-display" data-item-key={ACCOUNTS.display.id}>
                  <span className="xg-settings-title">{ACCOUNTS.display.label}</span>
                  <span className="xg-settings-scope">{SCOPE_LABELS.read_only}</span>
                  <div className="xg-settings-row xg-settings-accounts-info">
                    <span data-testid="setting-accounts-actor">{actor === "" ? "未提供" : actor}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
          {/* ★A-034①（20261006 大王令「无论实现与否都应剔除」）：原常驻滚动面尾的 S1–S3「机制在位」
              说明文案已删——机制**不自陈在 UI 上**（S1 同源／S2 无第二写路径／S3 作用面约束由
              `theme/preferences.ts` 承担并机判）。 */}
        </div>
      </div>
    </div>
  );
}

/** 组存在性自证（面板 ↔ 声明同源；供测试直接调用） */
export function settingsGroupIds(): string[] {
  return SHELL_DECL.settings.groups.map((g) => g.id);
}

export { settingGroup };
